import { TTransactionCurrency } from './finance.interfaces';
import { TActions } from '../__shared/shared.interfaces';
import { TDocument } from '../documents/documents.interfaces';

export type TFinancialGrantResponse = {
  data: TFinancialGrant[];
  total: number;
  page: number;
  size: number;
};

export type TFinancialGrant = {
  id: string;
  name: string;
  description: string;

  // Financial details
  amount: number;
  currency: TTransactionCurrency;
  disbursementFrequency: 'one-time' | 'monthly' | 'quarterly' | 'yearly';

  // Grant type and category
  grantType:
  | 'scholarship'
  | 'merit-based'
  | 'need-based'
  | 'research'
  | 'athletic'
  | 'minority'
  | 'other';
  category: string;
  tags?: string[];

  // Eligibility
  eligibilityCriteria: {
    gpaRequirement?: number;
    incomeThreshold?: number;
    documentsRequired?: string[];
    additionalRequirements?: string[];
  };

  // Time-related
  applicationDeadline: Date | string;
  awardDate?: Date | string;
  validity?: {
    startDate: Date | string;
    endDate?: Date | string;
  };

  // Provider and recipient
  providerId: string;
  providerName: string;
  providerType:
  | 'government'
  | 'private'
  | 'institution'
  | 'foundation'
  | 'corporate';
  recipientId?: string;
  recipientName?: string;

  // Status tracking
  status: 'open' | 'closed' | 'awarded' | 'disbursed' | 'expired';
  applicationStatus?: 'pending' | 'under-review' | 'approved' | 'rejected';

  documents: TDocument[];

  // Audit information
  actions: TActions;
};

export enum GrantType {
  SCHOLARSHIP = 'scholarship',
  MERIT_BASED = 'merit-based',
  NEED_BASED = 'need-based',
  RESEARCH = 'research',
  ATHLETIC = 'athletic',
  MINORITY = 'minority',
  OTHER = 'other',
}

export enum GrantStatus {
  ALL = 'all',
  OPEN = 'open',
  CLOSED = 'closed',
  AWARDED = 'awarded',
  DISBURSED = 'disbursed',
  EXPIRED = 'expired',
}

export enum ApplicationStatus {
  PENDING = 'pending',
  UNDER_REVIEW = 'under-review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum ProviderType {
  GOVERNMENT = 'government',
  PRIVATE = 'private',
  INSTITUTION = 'institution',
  FOUNDATION = 'foundation',
  CORPORATE = 'corporate',
}

export enum DisbursementFrequency {
  ONE_TIME = 'one-time',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly',
}

// ---------------- Grant List Query Params -----------------
export interface GrantListParams {
  page: number;
  limit: number;
  grantType?: GrantType | string;
  status?: GrantStatus | string;
  applicationStatus?: ApplicationStatus | string;
  providerType?: ProviderType | string;
  disbursementFrequency?: DisbursementFrequency | string;
  minAmount?: number;
  maxAmount?: number;
  currency?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ---------------- Grant Create/Update Request -----------------
export interface TGrantCreateRequest {
  name: string;
  description: string;
  amount: number;
  currency: string;
  disbursementFrequency: DisbursementFrequency | string;
  grantType: GrantType | string;
  category: string;
  tags?: string[];
  eligibilityCriteria: {
    gpaRequirement?: number;
    incomeThreshold?: number;
    documentsRequired?: string[];
    additionalRequirements?: string[];
  };
  applicationDeadline: Date | string;
  awardDate?: Date | string;
  validity?: {
    startDate: Date | string;
    endDate?: Date | string;
  };
  providerId: string;
  providerName: string;
  providerType: ProviderType | string;
  recipientId?: string;
  recipientName?: string;
  status: GrantStatus | string;
  applicationStatus?: ApplicationStatus | string;
}

export interface TGrantUpdateRequest extends TGrantCreateRequest {
  id: string;
}
